// src/components/Plans.jsx
// Tab "Pläne" der Projekt-Detailansicht: Planliste mit Upload und Löschen.
// US-PLAN-001: Datenmodell, Upload, Liste, Löschen.
// US-PLAN-002 (folgt): Klick auf einen Plan öffnet den PlanViewer.
import React, { useState, useEffect, useCallback } from "react";
import {
  getPlansWithVersions,
  createPlan,
  addPlanVersion,
  updatePlan,
  deletePlan,
} from "../services/azurePlansService";
import { carryOverMarkupsToNewVersion } from "../services/azurePendenzenMarkupsService";
import { useAuth } from "../hooks/useAuth";
import PlanViewer from "./PlanViewer";
import "../styles/plans.scss";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const emptyForm = () => ({
  name: "",
  geschoss: "",
  massstab: "",
  revision: "A",
  ausgabedatum: "",
  file: null,
});

// Massstab-Nenner (z.B. 100) als "1:100" anzeigen; null/0 -> "–"
const formatMassstab = (denom) => (denom ? `1:${denom}` : "–");

// --- Plan-Metadaten bearbeiten (Name, Geschoss, Massstab) --------------------
const EditPlanModal = ({ plan, onClose, onSave, saving, error }) => {
  const [name, setName] = useState(plan.name || "");
  const [geschoss, setGeschoss] = useState(plan.geschoss || "");
  const [massstab, setMassstab] = useState(plan.massstab ? `1:${plan.massstab}` : "");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ name, geschoss, massstab });
  };

  return (
    <div className="plans__modal-overlay" onClick={onClose}>
      <div className="plans__modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="plans__modal-header">
            <h3>Plan bearbeiten: {plan.name}</h3>
            <button type="button" className="plans__modal-close" onClick={onClose} aria-label="Schliessen">
              ×
            </button>
          </div>
          <div className="plans__modal-body">
            {error && <div className="plans__error">{error}</div>}
            <div className="plans__form-grid">
              <label className="plans__field">
                <span>Name</span>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
              </label>
              <label className="plans__field">
                <span>Geschoss / Ebene</span>
                <input type="text" value={geschoss} onChange={(e) => setGeschoss(e.target.value)} />
              </label>
              <label className="plans__field">
                <span>Massstab</span>
                <input
                  type="text"
                  value={massstab}
                  onChange={(e) => setMassstab(e.target.value)}
                  placeholder="z.B. 1:100"
                />
                <small className="plans__hint">
                  Für das Messen im Plan (nur bei PDF-Plänen wirksam).
                </small>
              </label>
            </div>
          </div>
          <div className="plans__modal-footer">
            <button type="button" className="plans__btn" onClick={onClose} disabled={saving}>
              Abbrechen
            </button>
            <button type="submit" className="plans__btn plans__btn--primary" disabled={saving}>
              {saving ? "Speichern..." : "Speichern"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Neue Version zu einem bestehenden Plan hochladen (US-PLAN-006) ----------
const NewVersionModal = ({ item, onClose, onSave, saving, error }) => {
  const [revision, setRevision] = useState("");
  const [ausgabedatum, setAusgabedatum] = useState("");
  const [file, setFile] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) return;
    onSave({ revision, ausgabedatum, file });
  };

  return (
    <div className="plans__modal-overlay" onClick={onClose}>
      <div className="plans__modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="plans__modal-header">
            <h3>Neue Version: {item.plan.name}</h3>
            <button type="button" className="plans__modal-close" onClick={onClose} aria-label="Schliessen">
              ×
            </button>
          </div>
          <div className="plans__modal-body">
            {error && <div className="plans__error">{error}</div>}
            <p className="plans__hint">
              Bisherige Revision: {item.activeVersion?.revision || "–"}. Bestehende
              Markierungen werden auf die neue Version übernommen und zur
              Positionsprüfung markiert.
            </p>
            <div className="plans__form-grid">
              <label className="plans__field">
                <span>Neue Revision</span>
                <input type="text" value={revision} onChange={(e) => setRevision(e.target.value)} required />
              </label>
              <label className="plans__field">
                <span>Ausgabedatum</span>
                <input
                  type="date"
                  value={ausgabedatum}
                  onChange={(e) => setAusgabedatum(e.target.value)}
                />
              </label>
              <label className="plans__field plans__field--full">
                <span>Datei (PDF, PNG, JPG)</span>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>
          </div>
          <div className="plans__modal-footer">
            <button type="button" className="plans__btn" onClick={onClose} disabled={saving}>
              Abbrechen
            </button>
            <button type="submit" className="plans__btn plans__btn--primary" disabled={saving}>
              {saving ? "Hochladen..." : "Hochladen"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Versionshistorie eines Plans (US-PLAN-006) -------------------------------
const VersionHistory = ({ versions }) => (
  <ul className="plans__version-history">
    {[...versions].reverse().map((v) => (
      <li key={v.id}>
        <span className="plans__version-rev">Rev. {v.revision}</span>
        <span>{formatDate(v.ausgabedatum)}</span>
        <span>{v.hochgeladenVon}</span>
        {v.superseded ? (
          <span className="plans__version-badge">ersetzt</span>
        ) : (
          <span className="plans__version-badge plans__version-badge--current">aktuell</span>
        )}
      </li>
    ))}
  </ul>
);

const Plans = ({ project, readOnly = false }) => {
  const { user } = useAuth();
  const projectUuid = project?.id;
  const [items, setItems] = useState([]); // [{ plan, versions, activeVersion }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [openPlan, setOpenPlan] = useState(null); // { plan, versions } oder null
  const [versionModalItem, setVersionModalItem] = useState(null); // { plan, versions, activeVersion }
  const [versionSaving, setVersionSaving] = useState(false);
  const [versionError, setVersionError] = useState(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [editPlan, setEditPlan] = useState(null); // Plan-Objekt oder null
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const loadData = useCallback(async () => {
    if (!projectUuid) return;
    try {
      setLoading(true);
      setError(null);
      const plansWithVersions = await getPlansWithVersions(projectUuid);
      const withVersions = plansWithVersions.map(({ plan, versions }) => {
        const activeVersion =
          versions.find((v) => v.id === plan.aktiveVersionId) ||
          versions[versions.length - 1] ||
          null;
        return { plan, versions, activeVersion };
      });
      setItems(withVersions);
    } catch (err) {
      setError(err.message || "Die Pläne konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [projectUuid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFormChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const resetForm = () => {
    setForm(emptyForm());
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Bitte einen Namen erfassen.");
      return;
    }
    if (!form.file) {
      setError("Bitte eine Datei auswählen.");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await createPlan(
        projectUuid,
        {
          name: form.name,
          geschoss: form.geschoss,
          massstab: form.massstab,
          revision: form.revision,
          ausgabedatum: form.ausgabedatum,
        },
        form.file,
        user?.email || user?.userDetails || ""
      );
      resetForm();
      await loadData();
    } catch (err) {
      setError(err.message || "Der Plan konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddVersion = async ({ revision, ausgabedatum, file }) => {
    const { plan, activeVersion } = versionModalItem;
    try {
      setVersionSaving(true);
      setVersionError(null);
      const { version } = await addPlanVersion(
        projectUuid,
        plan,
        { revision, ausgabedatum },
        file,
        user?.email || user?.userDetails || ""
      );
      if (activeVersion?.id) {
        await carryOverMarkupsToNewVersion(activeVersion.id, version.id);
      }
      setVersionModalItem(null);
      await loadData();
    } catch (err) {
      setVersionError(err.message || "Die neue Version konnte nicht gespeichert werden.");
    } finally {
      setVersionSaving(false);
    }
  };

  const handleUpdatePlan = async ({ name, geschoss, massstab }) => {
    if (!name || !name.trim()) {
      setEditError("Bitte einen Namen erfassen.");
      return;
    }
    try {
      setEditSaving(true);
      setEditError(null);
      await updatePlan(projectUuid, editPlan.id, { name, geschoss, massstab });
      setEditPlan(null);
      await loadData();
    } catch (err) {
      setEditError(err.message || "Der Plan konnte nicht gespeichert werden.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (
      !window.confirm(
        `Plan „${plan.name}" wirklich unwiderruflich löschen? Alle Versionen und Markierungen darauf gehen verloren.`
      )
    ) {
      return;
    }
    try {
      setError(null);
      await deletePlan(projectUuid, plan.id);
      await loadData();
    } catch (err) {
      setError(err.message || "Der Plan konnte nicht gelöscht werden.");
    }
  };

  if (loading) {
    return <div className="plans plans--loading">Pläne werden geladen...</div>;
  }

  return (
    <div className="plans">
      <div className="plans__header">
        <h3>Pläne</h3>
        {!readOnly && (
          <button
            type="button"
            className="plans__btn plans__btn--primary"
            onClick={() => setShowForm((v) => !v)}
          >
            + Neuer Plan
          </button>
        )}
      </div>

      {error && (
        <div className="plans__error" role="alert" onClick={() => setError(null)}>
          {error}
        </div>
      )}

      {showForm && (
        <form className="plans__form" onSubmit={handleSubmit}>
          <div className="plans__form-grid">
            <label className="plans__field">
              <span>Name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
                placeholder="z.B. Grundriss EG"
                required
              />
            </label>
            <label className="plans__field">
              <span>Geschoss / Ebene</span>
              <input
                type="text"
                value={form.geschoss}
                onChange={(e) => handleFormChange("geschoss", e.target.value)}
                placeholder="z.B. EG"
              />
            </label>
            <label className="plans__field">
              <span>Massstab</span>
              <input
                type="text"
                value={form.massstab}
                onChange={(e) => handleFormChange("massstab", e.target.value)}
                placeholder="z.B. 1:100"
              />
            </label>
            <label className="plans__field">
              <span>Revision</span>
              <input
                type="text"
                value={form.revision}
                onChange={(e) => handleFormChange("revision", e.target.value)}
              />
            </label>
            <label className="plans__field">
              <span>Ausgabedatum</span>
              <input
                type="date"
                value={form.ausgabedatum}
                onChange={(e) => handleFormChange("ausgabedatum", e.target.value)}
              />
            </label>
            <label className="plans__field plans__field--full">
              <span>Datei (PDF, PNG, JPG)</span>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                onChange={(e) => handleFormChange("file", e.target.files?.[0] || null)}
              />
              <small className="plans__hint">
                DWG bitte vorab als PDF exportieren.
              </small>
            </label>
          </div>
          <div className="plans__form-actions">
            <button
              type="button"
              className="plans__btn"
              onClick={resetForm}
              disabled={saving}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="plans__btn plans__btn--primary"
              disabled={saving}
            >
              {saving ? "Hochladen..." : "Hochladen"}
            </button>
          </div>
        </form>
      )}

      <div className="plans__table-wrapper">
        <table className="plans__table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Geschoss</th>
              <th>Massstab</th>
              <th>Revision</th>
              <th>Ausgabedatum</th>
              <th>Hochgeladen von</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td className="plans__empty" colSpan={7}>
                  Noch keine Pläne hochgeladen.
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const { plan, versions, activeVersion } = item;
                return (
                <React.Fragment key={plan.id}>
                <tr
                  className="plans__row"
                  onClick={() => setOpenPlan({ plan, versions })}
                  title="Zum Ansehen anklicken"
                >
                  <td className="plans__name">{plan.name}</td>
                  <td>{plan.geschoss}</td>
                  <td>{formatMassstab(plan.massstab)}</td>
                  <td>{activeVersion?.revision || "–"}</td>
                  <td>{formatDate(activeVersion?.ausgabedatum)}</td>
                  <td>{activeVersion?.hochgeladenVon || plan.erstelltVon}</td>
                  <td className="plans__row-actions">
                    {versions.length > 1 && (
                      <button
                        type="button"
                        className="plans__btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedHistoryId((id) => (id === plan.id ? null : plan.id));
                        }}
                      >
                        Historie
                      </button>
                    )}
                    {!readOnly && (
                      <button
                        type="button"
                        className="plans__btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditError(null);
                          setEditPlan(plan);
                        }}
                      >
                        Bearbeiten
                      </button>
                    )}
                    {!readOnly && (
                      <button
                        type="button"
                        className="plans__btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setVersionModalItem(item);
                        }}
                      >
                        Neue Version
                      </button>
                    )}
                    {!readOnly && (
                      <button
                        type="button"
                        className="plans__btn plans__btn--danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(plan);
                        }}
                      >
                        Löschen
                      </button>
                    )}
                  </td>
                </tr>
                {expandedHistoryId === plan.id && (
                  <tr className="plans__history-row">
                    <td colSpan={7}>
                      <VersionHistory versions={versions} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {openPlan && (
        <PlanViewer
          plan={openPlan.plan}
          versions={openPlan.versions}
          project={project}
          readOnly={readOnly}
          onClose={() => setOpenPlan(null)}
        />
      )}

      {versionModalItem && (
        <NewVersionModal
          item={versionModalItem}
          onClose={() => {
            setVersionModalItem(null);
            setVersionError(null);
          }}
          onSave={handleAddVersion}
          saving={versionSaving}
          error={versionError}
        />
      )}

      {editPlan && (
        <EditPlanModal
          plan={editPlan}
          onClose={() => {
            setEditPlan(null);
            setEditError(null);
          }}
          onSave={handleUpdatePlan}
          saving={editSaving}
          error={editError}
        />
      )}
    </div>
  );
};

export default Plans;
